import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertCircle,
  Bell,
  CheckCircle2,
  Clock,
  Crosshair,
  Home,
  MapPin,
  MessageSquare,
  Phone,
  Radio,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Siren,
  User,
  X,
  XCircle,
} from 'lucide-react';
import { Button } from '@shared/ui/primitives/Button';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { toast } from '@shared/ui/feedback/Toast';
import {
  acknowledgePanicLog,
  dialTarget,
  fetchPanicLogDetail,
  isActiveStatus,
  needsAcknowledgement,
  resolvePanicLog,
  type PanicLogDetail,
  type PanicLogDispatchAttempt,
  type PanicLogTimelineEntry,
} from './api';
import { PanicMap } from './PanicMap';

/* ── Helpers ─────────────────────────────────────────────────────────── */

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function formatElapsed(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '';
  if (ms < 1000) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function statusTone(status: string): { label: string; chip: string; dot: string } {
  const s = status.toLowerCase();
  if (s === 'triggered') return { label: 'Triggered', chip: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s === 'dispatched') return { label: 'Dispatched', chip: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' };
  if (s === 'acknowledged') return { label: 'Acknowledged', chip: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' };
  if (s === 'in_progress') return { label: 'In progress', chip: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' };
  if (s === 'escalated') return { label: 'Escalated', chip: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500' };
  if (s === 'resolved') return { label: 'Resolved', chip: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' };
  if (s === 'cancelled') return { label: 'Cancelled', chip: 'bg-neutral-100 text-neutral-700 border-neutral-200', dot: 'bg-neutral-400' };
  return { label: status.replace(/_/g, ' '), chip: 'bg-neutral-100 text-neutral-700 border-neutral-200', dot: 'bg-neutral-400' };
}

function dispatchTone(status: string | undefined | null): { label: string; chip: string; alert: boolean } {
  const s = (status ?? '').toLowerCase();
  if (s === 'sent' || s === 'success') return { label: 'Dispatch sent', chip: 'bg-green-100 text-green-800 border-green-200', alert: false };
  if (s === 'partial') return { label: 'Dispatch partial', chip: 'bg-amber-100 text-amber-800 border-amber-200', alert: true };
  if (s === 'failed') return { label: 'Dispatch failed', chip: 'bg-red-100 text-red-800 border-red-200', alert: true };
  if (s === 'skipped') return { label: 'Dispatch skipped', chip: 'bg-neutral-100 text-neutral-700 border-neutral-200', alert: false };
  return { label: status ? `Dispatch ${status}` : 'Dispatch pending', chip: 'bg-neutral-100 text-neutral-700 border-neutral-200', alert: false };
}

function channelIcon(channel: string): ReactNode {
  const c = channel.toLowerCase();
  if (c === 'sms') return <MessageSquare className="h-4 w-4" strokeWidth={2} />;
  if (c === 'voice' || c === 'call') return <Phone className="h-4 w-4" strokeWidth={2} />;
  if (c === 'push' || c === 'notification') return <Bell className="h-4 w-4" strokeWidth={2} />;
  return <Radio className="h-4 w-4" strokeWidth={2} />;
}

function timelineIcon(item: PanicLogTimelineEntry): ReactNode {
  if (item.type === 'status') return <Activity className="h-3.5 w-3.5 text-blue-600" strokeWidth={2.5} />;
  if (item.type === 'event') return <AlertCircle className="h-3.5 w-3.5 text-amber-600" strokeWidth={2.5} />;
  if (item.type === 'location') return <MapPin className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2.5} />;
  if (item.type === 'dispatch') {
    const ok = (item.status ?? '').toLowerCase() !== 'failed';
    return ok
      ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" strokeWidth={2.5} />
      : <XCircle className="h-3.5 w-3.5 text-red-600" strokeWidth={2.5} />;
  }
  return <Activity className="h-3.5 w-3.5 text-neutral-500" strokeWidth={2} />;
}

/* ── Page ────────────────────────────────────────────────────────────── */

/**
 * Renders as a right-side drawer overlaid on top of the NOC dashboard, so
 * the dashboard stays mounted — polling, the alarm loop and the live tile
 * counters continue while a guard drills into one incident. ESC and the
 * close button dismiss the drawer; closing navigates to `/`, leaving the
 * dashboard exactly where the guard left it.
 *
 * Width: full on mobile, fixed ~640px on md+ so the dashboard remains
 * visible to the left of the drawer.
 */
export function IncidentDetailPage(): JSX.Element {
  const { logId } = useParams<{ logId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [resolveNote, setResolveNote] = useState('');

  // The drawer opens from the board and from History, so return to whichever
  // pushed it; a deep-linked drawer has nothing to go back to and lands on the board.
  const close = (): void => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/', { replace: true });
  };

  // ESC closes the drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['panic', 'log', logId],
    queryFn: () => fetchPanicLogDetail(logId!),
    enabled: !!logId,
  });

  const refreshBoards = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['panic', 'active'] });
    void queryClient.invalidateQueries({ queryKey: ['panic', 'history'] });
    void queryClient.invalidateQueries({ queryKey: ['panic', 'log', logId] });
  };

  const ack = useMutation({
    mutationFn: () => acknowledgePanicLog(logId!, note),
    onSuccess: () => {
      toast.success('Incident acknowledged');
      // Stay open: the refetched incident swaps the Acknowledge card for
      // Resolve, so the guard closes the incident without re-opening it.
      refreshBoards();
    },
    onError: (err: Error) => toast.danger(err.message || 'Acknowledge failed'),
  });

  const resolve = useMutation({
    mutationFn: () => resolvePanicLog(logId!, resolveNote),
    onSuccess: () => {
      toast.success('Incident resolved');
      refreshBoards();
      close();
    },
    onError: (err: Error) => toast.danger(err.message || 'Resolve failed'),
  });

  if (!logId) {
    return <p className="p-4 text-neutral-600">Missing incident ID</p>;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Incident ${data?.emergency_type_name ?? ''}`}
      className="fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-neutral-200 bg-white shadow-2xl animate-in slide-in-from-right duration-200 md:w-[640px]"
    >
      {/* Drawer header */}
      <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3">
        <button
          type="button"
          onClick={close}
          aria-label="Close incident detail"
          className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
          Close
          <kbd className="ml-1 hidden rounded border border-neutral-300 bg-neutral-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-neutral-500 md:inline-block">
            Esc
          </kbd>
        </button>
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Drawer body */}
      <div className="flex-1 overflow-y-auto px-4 py-5">
        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-72 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}

        {isError && (
          <ErrorState
            title="Could not load incident"
            description={error instanceof Error ? error.message : 'Request failed'}
            onRetry={() => void refetch()}
          />
        )}

        {data && (
          <IncidentBody
            data={data}
            note={note}
            setNote={setNote}
            onAcknowledge={() => ack.mutate()}
            acknowledging={ack.isPending}
            resolveNote={resolveNote}
            setResolveNote={setResolveNote}
            onResolve={() => resolve.mutate()}
            resolving={resolve.isPending}
          />
        )}
      </div>
    </div>
  );
}

/* ── Body ────────────────────────────────────────────────────────────── */

interface BodyProps {
  data: PanicLogDetail;
  note: string;
  setNote: (s: string) => void;
  onAcknowledge: () => void;
  acknowledging: boolean;
  resolveNote: string;
  setResolveNote: (s: string) => void;
  onResolve: () => void;
  resolving: boolean;
}

function IncidentBody({
  data,
  note,
  setNote,
  onAcknowledge,
  acknowledging,
  resolveNote,
  setResolveNote,
  onResolve,
  resolving,
}: BodyProps): JSX.Element {
  const status = statusTone(String(data.status));
  const dispatch = dispatchTone(data.dispatch_status);
  const canAck = needsAcknowledgement(data);
  const alreadyAck = !!data.acknowledged_at || String(data.status).toLowerCase() === 'acknowledged';
  // Glovent parity: resolve only becomes available once the alert has been acknowledged.
  const canResolve = !canAck && isActiveStatus(String(data.status));
  const tel = dialTarget(data.triggered_by_phone);

  const lat = data.latitude !== null && data.latitude !== undefined ? Number(data.latitude) : null;
  const lng = data.longitude !== null && data.longitude !== undefined ? Number(data.longitude) : null;
  const hasCoords =
    lat !== null && !Number.isNaN(lat) && lng !== null && !Number.isNaN(lng);
  const accuracy =
    (data.metadata?.accuracy_m ?? data.locations?.[0]?.accuracy_m ?? null) as number | null;

  const summary = data.metadata?.dispatch_summary;
  const recipients = data.dispatch_attempts ?? [];

  // Timeline newest-first so the most recent activity is at the top.
  const timeline = [...(data.timeline ?? [])].sort((a, b) => {
    const ta = new Date(a.created_at).getTime();
    const tb = new Date(b.created_at).getTime();
    return tb - ta;
  });

  return (
    <div className="space-y-5">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Siren className="h-5 w-5 text-red-600" strokeWidth={2} />
              <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                Emergency
              </span>
            </div>
            <h1 className="mt-1 text-2xl font-extrabold leading-tight text-ink">
              {data.emergency_type_name}
            </h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-600">
              <span className="inline-flex items-center gap-1.5">
                <User className="h-4 w-4 text-neutral-400" strokeWidth={2} />
                {data.triggered_by_name?.trim() || 'Unknown member'}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-neutral-400" strokeWidth={2} />
                {formatElapsed(data.created_at)}
                <span className="text-neutral-400">·</span>
                {formatDateTime(data.created_at)}
              </span>
              {data.triggered_by_address?.trim() ? (
                <span className="inline-flex items-center gap-1.5">
                  <Home className="h-4 w-4 text-neutral-400" strokeWidth={2} />
                  {data.triggered_by_address}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${status.chip}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
              {status.label}
            </span>
            {data.notification_level ? (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
                Level · {data.notification_level.replace(/_/g, ' ')}
              </span>
            ) : null}
          </div>
        </div>

        {/* Phone the alerter — the guard's fastest first move. */}
        {tel ? (
          <a
            href={tel}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-green-700"
          >
            <Phone className="h-4 w-4" strokeWidth={2.5} />
            Phone alerter · {data.triggered_by_phone}
          </a>
        ) : (
          <p className="mt-4 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-500">
            No contact number on file for this member.
          </p>
        )}
      </div>

      {/* ── Acknowledge ────────────────────────────────────────── */}
      {canAck && (
        <div className="rounded-xl border-2 border-red-300 bg-red-50 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-600" strokeWidth={2.5} />
            <h2 className="text-sm font-bold uppercase tracking-wider text-red-800">
              Action required
            </h2>
          </div>
          <p className="mb-4 text-sm text-red-800">
            This incident is awaiting acknowledgement. Confirm receipt and add an optional note for the audit trail.
          </p>
          <div>
            <label
              htmlFor="ack-note"
              className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-red-700"
            >
              Acknowledgement note{' '}
              <span className="font-medium normal-case tracking-normal text-red-500/80">
                (optional)
              </span>
            </label>
            <textarea
              id="ack-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Guard dispatched, on route to address"
              className="w-full resize-y rounded-md border border-red-200 bg-white px-3 py-2 text-sm text-ink placeholder:text-neutral-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20"
            />
          </div>
          <Button
            variant="primary"
            className="mt-3 w-full bg-red-600 text-white hover:bg-red-700"
            disabled={acknowledging}
            onClick={onAcknowledge}
          >
            {acknowledging ? 'Acknowledging…' : 'Acknowledge alert'}
          </Button>
        </div>
      )}

      {alreadyAck && !canAck && (
        <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-800">
          <CheckCircle2 className="h-5 w-5 text-green-600" strokeWidth={2.5} />
          Acknowledged{data.acknowledged_at ? ` at ${formatDateTime(data.acknowledged_at)}` : ''}
        </div>
      )}

      {/* ── Resolve ────────────────────────────────────────────── */}
      {canResolve && (
        <ResolveCard
          note={resolveNote}
          setNote={setResolveNote}
          onResolve={onResolve}
          resolving={resolving}
        />
      )}

      {/* ── Map ────────────────────────────────────────────────── */}
      {hasCoords && (
        <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
          <PanicMap lat={lat!} lng={lng!} accuracyM={accuracy} />
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-200 px-4 py-3 text-xs">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-neutral-600">
              <span className="inline-flex items-center gap-1.5 font-mono text-neutral-700">
                <Crosshair className="h-3.5 w-3.5 text-neutral-400" strokeWidth={2} />
                {lat!.toFixed(5)}, {lng!.toFixed(5)}
              </span>
              {accuracy ? (
                <span className="text-neutral-500">±{Math.round(accuracy)} m accuracy</span>
              ) : null}
              {data.metadata?.source ? (
                <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-600">
                  source · {data.metadata.source.replace(/_/g, ' ')}
                </span>
              ) : null}
            </div>
            <a
              href={`https://www.google.com/maps?q=${lat},${lng}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-semibold text-red-600 hover:underline"
            >
              <MapPin className="h-3.5 w-3.5" strokeWidth={2} />
              Open in Google Maps
            </a>
          </div>
        </div>
      )}

      {/* ── Dispatch ────────────────────────────────────────────── */}
      {(summary || recipients.length > 0) && (
        <div
          className={`rounded-xl border bg-white p-5 ${
            dispatch.alert ? 'border-red-200 shadow-sm' : 'border-neutral-200'
          }`}
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Radio className="h-5 w-5 text-neutral-700" strokeWidth={2} />
              <h2 className="text-sm font-bold uppercase tracking-wider text-ink">
                Notification dispatch
              </h2>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${dispatch.chip}`}
            >
              {dispatch.label}
            </span>
          </div>

          {summary && (
            <div className="mb-4 grid grid-cols-3 gap-3">
              <SummaryStat label="Attempted" value={summary.attempted} tone="neutral" />
              <SummaryStat label="Sent" value={summary.sent} tone={summary.sent > 0 ? 'green' : 'neutral'} />
              <SummaryStat label="Failed" value={summary.failed} tone={summary.failed > 0 ? 'red' : 'neutral'} />
            </div>
          )}

          {summary?.skipped && summary.reason ? (
            <p className="mb-4 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-700">
              <strong className="font-semibold">Skipped:</strong> {summary.reason}
            </p>
          ) : null}

          {recipients.length > 0 && (
            <div className="space-y-2">
              {recipients.map((r) => (
                <DispatchRow key={r.panic_log_dispatch_attempt_id} attempt={r} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Timeline ───────────────────────────────────────────── */}
      {timeline.length > 0 && (
        <div className="rounded-xl border border-neutral-200 bg-white p-5">
          <div className="mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-neutral-700" strokeWidth={2} />
            <h2 className="text-sm font-bold uppercase tracking-wider text-ink">Timeline</h2>
            <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-600 tabular-nums">
              {timeline.length}
            </span>
          </div>
          <ol className="relative space-y-4 pl-7 before:absolute before:left-[10px] before:top-2 before:bottom-2 before:w-px before:bg-neutral-200">
            {timeline.map((entry) => (
              <li key={entry.id} className="relative">
                <span className="absolute -left-7 top-0 flex h-5 w-5 items-center justify-center rounded-full border border-neutral-200 bg-white">
                  {timelineIcon(entry)}
                </span>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-sm font-semibold text-ink">{entry.title}</p>
                  <span className="text-[11px] font-medium text-neutral-500 tabular-nums">
                    {formatElapsed(entry.created_at)}
                  </span>
                </div>
                {entry.message ? (
                  <p className="mt-0.5 text-sm text-neutral-600">{entry.message}</p>
                ) : null}
                {entry.note ? (
                  <p className="mt-1.5 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-1.5 text-xs text-neutral-700">
                    {entry.note}
                  </p>
                ) : null}
                <p className="mt-1 text-[11px] text-neutral-400">
                  {entry.actor_name ?? 'System'} · {formatDateTime(entry.created_at)}
                </p>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

/* ── Pieces ──────────────────────────────────────────────────────────── */

/**
 * Closing an incident is irreversible, so it takes two deliberate taps and a
 * written summary — the note is also required by the tenant API.
 */
function ResolveCard({
  note,
  setNote,
  onResolve,
  resolving,
}: {
  note: string;
  setNote: (s: string) => void;
  onResolve: () => void;
  resolving: boolean;
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const ready = note.trim().length > 0;

  return (
    <div className="rounded-xl border-2 border-green-300 bg-green-50 p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-green-700" strokeWidth={2.5} />
        <h2 className="text-sm font-bold uppercase tracking-wider text-green-800">Resolve panic</h2>
      </div>
      <label
        htmlFor="resolve-note"
        className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-green-800"
      >
        Summary <span className="font-medium normal-case tracking-normal text-green-700/80">(required)</span>
      </label>
      <textarea
        id="resolve-note"
        rows={3}
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
          setConfirming(false);
        }}
        placeholder="Please give a short summary of the panic"
        className="w-full resize-y rounded-md border border-green-200 bg-white px-3 py-2 text-sm text-ink placeholder:text-neutral-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
      />

      {confirming ? (
        <div className="mt-3">
          <p className="mb-2 text-sm font-semibold text-green-900">
            Are you sure you wish to resolve this panic?
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              className="flex-1"
              disabled={resolving}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="flex-1 bg-green-700 text-white hover:bg-green-800"
              disabled={resolving}
              onClick={onResolve}
            >
              {resolving ? 'Resolving…' : 'Confirm resolve'}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="primary"
          className="mt-3 w-full bg-green-700 text-white hover:bg-green-800"
          disabled={!ready}
          onClick={() => setConfirming(true)}
        >
          Resolve panic
        </Button>
      )}
    </div>
  );
}

function SummaryStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'red' | 'green' | 'neutral';
}): JSX.Element {
  const toneClass = {
    red: 'bg-red-50 border-red-200 text-red-900',
    green: 'bg-green-50 border-green-200 text-green-900',
    neutral: 'bg-neutral-50 border-neutral-200 text-neutral-900',
  }[tone];
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${toneClass}`}>
      <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</p>
      <p className="mt-0.5 text-2xl font-extrabold leading-none tabular-nums">{value}</p>
    </div>
  );
}

function DispatchRow({ attempt }: { attempt: PanicLogDispatchAttempt }): JSX.Element {
  const failed = !attempt.success;
  return (
    <div
      className={`rounded-lg border p-3 ${
        failed ? 'border-red-200 bg-red-50/60' : 'border-green-200 bg-green-50/60'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={failed ? 'text-red-600' : 'text-green-600'}>
              {failed ? (
                <XCircle className="h-4 w-4" strokeWidth={2.5} />
              ) : (
                <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
              )}
            </span>
            <p className="truncate text-sm font-semibold text-ink">
              {attempt.recipient_name ?? 'Unknown recipient'}
            </p>
            {attempt.recipient_group_name ? (
              <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-600">
                {attempt.recipient_group_name}
              </span>
            ) : null}
          </div>
          <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-neutral-600">
            <span className="text-neutral-400">{channelIcon(attempt.channel)}</span>
            <span className="font-semibold uppercase tracking-wide">{attempt.channel}</span>
            <span className="text-neutral-300">·</span>
            <span className="font-mono">{attempt.target}</span>
          </div>
          {attempt.error ? (
            <p className="mt-2 rounded-md border border-red-200 bg-white px-2 py-1.5 text-xs text-red-700">
              <ShieldAlert className="mr-1 inline h-3 w-3" strokeWidth={2.5} />
              {attempt.error}
            </p>
          ) : null}
        </div>
        <span className="whitespace-nowrap text-[11px] text-neutral-500">
          {formatElapsed(attempt.created_at)}
        </span>
      </div>
    </div>
  );
}
